import Emitter from "es6-event-emitter";
import { CmpType } from "./../utilities/enums";
import Didomi from "./didomi";
import LmoCMP from "./lmo-cmp";
import SourcePointCMP from "./sourcepoint-cmp";

export default class CmpManager extends Emitter {
  constructor(cmpType, options) {
    super();
    this._options = options;
    this._cmpType = cmpType;
    this._initialization();
    this._eventHandler();
  }

  _initialization() {
    switch (this._cmpType) {
      case CmpType.DIDOMI:
        this._cmp = new Didomi(this._options);
        break;
      case CmpType.LMO:
        this._cmp = new LmoCMP();
        break;
      case CmpType.SOURCEPOINT:
        this._cmp = new SourcePointCMP(this._options);
        break;
      default:
        this._cmp = null;
    }
  }

  _eventHandler() {
    if (this._cmp) {
      this._cmp.on("userConsent:changed", () => {
        this.trigger("userConsent:changed");
      });
      this._cmp.on("cmp:loaded", () => {
        this.trigger("cmp:loaded");
      });
    }
  }

  get cmp() {
    return this._cmp;
  }

  // Only SourcePoint has been implemented. Any other CMP will return true unless they are implemented as well.
  isAdobeAnalyticsTrackingEnabled() {
    let trackingEnabled;

    if (this._options.features.adobe) {
      switch (this._cmpType) {
        case CmpType.SOURCEPOINT: {
          const purposeConsent = this._isPurposeEnabledId(
            this._options.adobe.purposeIds
          );
          const vendorConsent = this._cmp.hasUserGivenConsentForVendor(
            this._options.adobe.vendorId
          );
          trackingEnabled =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false;
          break;
        }
        default: {
          trackingEnabled = true;
        }
      }
    } else {
      trackingEnabled = true;
    }

    return trackingEnabled;
  }

  isGATrackingEnabled() {
    let track;
    if (this._options.features.GA) {
      switch (this._cmpType) {
        case CmpType.DIDOMI: {
          // this._cmp.hasUserGivenConsent(this._options.GA.purposeId) can be undefined (tel-tel case)
          const purposeConsent = this._isPurposeEnabledId(
            this._options.GA.purposeId
          );
          const vendorConsent = this._options.GA.vendorId
            ? this._cmp.hasUserGivenConsentForVendor(this._options.GA.vendorId)
            : true; //OFR case, if there is no vendorId set it to true
          //In case the consent is undefined return false to prevent tracking
          track =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false;
          break;
        }
        case CmpType.LMO:
          track =
            typeof (
              this._cmp.getConsent() && this._cmp.getPurposes().analytics
            ) !== "undefined"
              ? this._cmp.getConsent() && this._cmp.getPurposes().analytics
              : false;
          break;
        case CmpType.SOURCEPOINT: {
          const purposeConsent = this._isPurposeEnabledId(
            this._options.GA.purposeId
          );
          const vendorConsent = this._cmp.hasUserGivenConsentForVendor(
            this._options.GA.vendorId
          );
          track =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false;
          break;
        }
        default:
          track = true;
      }
    } else {
      track = true;
    }
    return track;
  }

  isERTrackingEnabled() {
    let track;
    if (this._options.features.ER) {
      switch (this._cmpType) {
        case CmpType.LMO:
          track =
            typeof (
              this._cmp.getConsent() && this._cmp.getPurposes().analytics
            ) !== "undefined"
              ? this._cmp.getConsent() && this._cmp.getPurposes().analytics
              : false;
          break;
        case CmpType.DIDOMI: {
          // this._cmp.hasUserGivenConsent(this._options.GA.purposeId) can be undefined
          const purposeConsent = this._isPurposeEnabledId(
            this._options.ER.purposeId
          );
          const vendorConsent = this._options.ER.vendorId
            ? this._cmp.hasUserGivenConsentForVendor(this._options.ER.vendorId)
            : true;
          track =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false; // if a feature is behind consent return false by default
          break;
        }
        case CmpType.SOURCEPOINT:
          /* dumont does not have any Ids for ER it uses a cookie to determine whether the user gave consent.
          in case of SWMH that has vendor and purpose Ids we check consent using those information
          */
          track = this._cmp.getConsent().engageReaders;

          if (!track) {
            const purposeConsent =
              this._options.ER && this._options.ER.purposeId
                ? this._isPurposeEnabledId(this._options.ER.purposeId)
                : false;
            const vendorConsent =
              this._options.ER && this._options.ER.vendorId
                ? this._cmp.hasUserGivenConsentForVendor(
                    this._options.ER.vendorId
                  )
                : false;
            track =
              typeof (purposeConsent && vendorConsent) !== "undefined"
                ? purposeConsent && vendorConsent
                : false;
          }
          break;
        default:
          track = true;
      }
    } else {
      track = true;
    }
    return track;
  }

  isSocialSharingTrackingEnabled() {
    let track;
    if (this._options.features.socialSharing) {
      switch (this._cmpType) {
        case CmpType.DIDOMI: {
          // this._cmp.hasUserGivenConsent(this._options.GA.purposeId) can be undefined
          const purposeConsent = this._isPurposeEnabledId(
            this._options.socialSharing.purposeId
          );
          const vendorConsent = this._options.socialSharing.vendorId
            ? this._cmp.hasUserGivenConsentForVendor(
                this._options.socialSharing.vendorId
              )
            : true;
          track =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false; // if a feature is behind consent return false by default
          break;
        }
        default:
          track = true;
      }
    } else {
      track = true;
    }
    return track;
  }

  isXitiTrackingEnabled() {
    let track;
    if (this._options.features.xiti) {
      switch (this._cmpType) {
        case CmpType.DIDOMI: {
          const purposeConsent = this._isPurposeEnabledId(
            this._options.Xiti.purposeId
          );
          const vendorConsent = this._options.Xiti.vendorId
            ? this._cmp.hasUserGivenConsentForVendor(
                this._options.Xiti.vendorId
              )
            : true;
          track =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false; // if a feature is behind consent return false by default
          break;
        }
        default:
          track = true;
      }
    } else {
      track = true;
    }
    return track;
  }

  isNMOTrackingEnabled() {
    let track;
    if (this._options.features.NMO) {
      switch (this._cmpType) {
        case CmpType.DIDOMI: {
          const purposeConsent = this._isPurposeEnabledId(
            this._options.NMO.purposeId
          );
          const vendorConsent = this._options.NMO.vendorId
            ? this._cmp.hasUserGivenConsentForVendor(this._options.NMO.vendorId)
            : true;
          track =
            typeof (purposeConsent && vendorConsent) !== "undefined"
              ? purposeConsent && vendorConsent
              : false; // if a feature is behind consent return false by default
          break;
        }
        default:
          track = true;
      }
    } else {
      track = true;
    }
    return track;
  }

  showPreference() {
    switch (this._cmpType) {
      case CmpType.DIDOMI:
        this._cmp.showPreferences("purposes");
        break;
      case CmpType.LMO:
        break;
      case CmpType.SOURCEPOINT:
        this._cmp.showPreferences(this._options.siteId);
        break;
      default:
        return;
    }
  }

  getUserConsentForGA() {
    switch (this._cmpType) {
      case CmpType.DIDOMI:
        return this._cmp.hasUserGivenConsentForVendor(
          this._options.GA.vendorId
        );
      case CmpType.SOURCEPOINT:
        return this._cmp.hasUserGivenConsentForVendor(
          this._options.GA.vendorId
        );
      default:
        return;
    }
  }

  _isPurposeEnabledId(purposeIds) {
    let isPurposedEnabled = false;
    for (const purposeId of purposeIds) {
      const consent = this._cmp.hasUserGivenConsent(purposeId);
      if (!consent) {
        return consent;
      } else {
        isPurposedEnabled = consent;
      }
    }
    return isPurposedEnabled;
  }
}



// WEBPACK FOOTER //
// ./src/common/cmp/cmpManager.js